/**
 * useLoadingDelay Hook
 * Prevents flash of loading state for fast operations
 *
 * Only shows loading UI if operation takes longer than the specified delay.
 * This creates a smoother user experience by avoiding jarring loading flashes
 * for operations that complete quickly.
 *
 * @param isLoading - The actual loading state from data fetching
 * @param delay - Minimum time (in ms) before showing loading UI (default: 200ms)
 * @returns Whether to show the loading UI
 *
 * @example
 * const { data, isLoading } = useQuery(...);
 * const showSkeleton = useLoadingDelay(isLoading, 200);
 *
 * if (showSkeleton) {
 *   return <Skeleton />;
 * }
 */

import { useState, useEffect } from 'react';

export function useLoadingDelay(isLoading: boolean, delay = 200): boolean {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isLoading) {
      // Start timer when loading begins
      const timer = setTimeout(() => setShow(true), delay);
      return () => clearTimeout(timer);
    } else {
      // Immediately hide when loading completes
      setShow(false);
    }
  }, [isLoading, delay]);

  return show;
}
